// SETTINGS V2 — Pricing card 3 (Záruka a podmienky) — SETTINGS_V2_SP7 §5.1.
// Presentational: 3 period selects + 3 conditional custom text fields + the mono
// sentinel footnote. Owns NO state — values + onChange callbacks come from
// PricingPageV2. Data wiring (sentinel reveal at 5/5/6, Number(v) coercion) is
// BINDING; the visual 2-col arrangement follows the designer's S2COL where it
// fits, but each reveal always sits directly under its OWN select.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { SCard, SSelectField, SInputField } from "../atoms";
import {
  WarrantyPeriodCode,
  PaymentTermsCode,
  ValidityPeriodCode,
  buildWarrantyOptions,
  buildPaymentOptions,
  buildValidityOptions,
} from "./pricingV2Data";

export interface PricingTermsCardV2Props {
  warrantyPeriodCode: number;
  paymentTermsCode: number;
  validityPeriodCode: number;
  customWarranty: string;
  customPaymentTerms: string;
  customValidity: string;
  onWarrantyChange: (v: number) => void;
  onPaymentChange: (v: number) => void;
  onValidityChange: (v: number) => void;
  onCustomWarrantyChange: (v: string) => void;
  onCustomPaymentChange: (v: string) => void;
  onCustomValidityChange: (v: string) => void;
  isPhone: boolean;
  animationDelay: string;
}

const PricingTermsCardV2: React.FC<PricingTermsCardV2Props> = ({
  warrantyPeriodCode,
  paymentTermsCode,
  validityPeriodCode,
  customWarranty,
  customPaymentTerms,
  customValidity,
  onWarrantyChange,
  onPaymentChange,
  onValidityChange,
  onCustomWarrantyChange,
  onCustomPaymentChange,
  onCustomValidityChange,
  isPhone,
  animationDelay,
}) => {
  const { translate: t } = useTranslation("settings");

  const twoCol: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: isPhone ? "1fr" : "1fr 1fr",
    gap: 12,
  };
  const revealMt: React.CSSProperties = { marginTop: 12 };

  return (
    <SCard
      title={t("settings:pricing.warrantyAndTerms")}
      style={{ animation: `sv2-card-reveal 0.4s ease-out ${animationDelay} both` }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Row 1 — warranty + payment; each reveal directly under its own select */}
        <div style={twoCol}>
          <div style={{ minWidth: 0 }}>
            <SSelectField
              label={t("settings:pricing.defaultWarranty")}
              value={warrantyPeriodCode}
              onChange={(v) => onWarrantyChange(Number(v))}
              options={buildWarrantyOptions(t)}
            />
            {warrantyPeriodCode === WarrantyPeriodCode.Custom /* 5 */ && (
              <div style={revealMt}>
                <SInputField
                  label={t("settings:pricing.customWarranty")}
                  value={customWarranty}
                  onChange={onCustomWarrantyChange}
                  placeholder={t("settings:pricing.customWarrantyPlaceholder")}
                />
              </div>
            )}
          </div>

          <div style={{ minWidth: 0 }}>
            <SSelectField
              label={t("settings:pricing.defaultPaymentTerms")}
              value={paymentTermsCode}
              onChange={(v) => onPaymentChange(Number(v))}
              options={buildPaymentOptions(t)}
            />
            {paymentTermsCode === PaymentTermsCode.Custom /* 5 */ && (
              <div style={revealMt}>
                <SInputField
                  label={t("settings:pricing.customPaymentTerms")}
                  value={customPaymentTerms}
                  onChange={onCustomPaymentChange}
                  placeholder={t("settings:pricing.customPaymentPlaceholder")}
                />
              </div>
            )}
          </div>
        </div>

        {/* Row 2 — validity (own reveal directly beneath) */}
        <div style={twoCol}>
          <div style={{ minWidth: 0 }}>
            <SSelectField
              label={t("settings:pricing.defaultValidity")}
              value={validityPeriodCode}
              onChange={(v) => onValidityChange(Number(v))}
              options={buildValidityOptions(t)}
            />
            {validityPeriodCode === ValidityPeriodCode.Custom /* 6 */ && (
              <div style={revealMt}>
                <SInputField
                  label={t("settings:pricing.customValidity")}
                  value={customValidity}
                  onChange={onCustomValidityChange}
                  placeholder={t("settings:pricing.customValidityPlaceholder")}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mono footnote — reveal hint only (designer line 33-35, i18n'd §9). */}
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9.5,
          color: "var(--faint)",
          letterSpacing: "0.04em",
          marginTop: 12,
        }}
      >
        {t("settings:pricing.sentinelFootnote")}
      </div>
    </SCard>
  );
};

export default PricingTermsCardV2;
